import React, { useState, useEffect, useMemo } from 'react';
import {
  FiActivity,
  FiCalendar,
  FiLoader,
  FiCheckSquare,
  FiAlertTriangle,
  FiLock,
  FiInfo,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormField } from '../common/FormField';
import { Button } from '../common/Button';
import { Select } from '../common/Select';
import { DateInput } from '../common/DateInput';
import { Modal } from '../common/Modal';
import { EnrichedWorklistItem } from '../../../helpers/api/dicom/dicom';
import {
  useUpdatePregnancy,
  PregnancyData,
} from '../../../helpers/api/pregnancy/pregnancy';
import { computeGAFromLmp } from '../NewCaseReverseUpload/utils';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';

const amberWarningAlertClass = (isDark: boolean, size: 'md' | 'sm' = 'md') => {
  if (size === 'sm') {
    return isDark
      ? 'flex gap-2 rounded-lg border border-amber-700 bg-amber-950 px-2.5 py-2 text-[11px] leading-relaxed text-amber-100'
      : 'flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-900';
  }
  return isDark
    ? 'flex gap-3 rounded-xl border border-amber-700 bg-amber-950 px-3.5 py-3 text-amber-50'
    : 'flex gap-3 rounded-xl border border-amber-200/90 bg-amber-50 px-3.5 py-3 text-amber-950';
};

type BioType = 'CRL' | 'BPD' | 'HC' | 'AC' | 'FL';
type Trimester = 'T1' | 'T2' | 'T3';

const TRIMESTER_META: Record<
  Trimester,
  {
    titleKey: string;
    rangeKey: string;
    chipClassLight: string;
    chipClassDark: string;
    cardClassLight: string;
    cardClassDark: string;
    footerDividerClassLight: string;
    footerDividerClassDark: string;
    labelAccentClassLight: string;
    labelAccentClassDark: string;
  }
> = {
  T1: {
    titleKey: 'pregnancy_modal_trimester_t1_title',
    rangeKey: 'pregnancy_modal_trimester_t1_range',
    chipClassLight:
      'bg-sky-600 text-white shadow-sm ring-1 ring-sky-500/30',
    chipClassDark:
      'bg-sky-600 text-white shadow-sm ring-1 ring-sky-500/40',
    cardClassLight:
      'border-sky-200/90 bg-gradient-to-br from-sky-50 via-sky-50/80 to-sky-100/60 shadow-md shadow-sky-200/30 ring-1 ring-sky-300/20',
    cardClassDark:
      'border-sky-600/50 bg-sky-950 shadow-md shadow-black/25 ring-1 ring-sky-500/25',
    footerDividerClassLight: 'border-sky-200/90',
    footerDividerClassDark: 'border-sky-700/50',
    labelAccentClassLight: 'text-sky-700',
    labelAccentClassDark: 'text-sky-300',
  },
  T2: {
    titleKey: 'pregnancy_modal_trimester_t2_title',
    rangeKey: 'pregnancy_modal_trimester_t2_range',
    chipClassLight:
      'bg-violet-600 text-white shadow-sm ring-1 ring-violet-500/30',
    chipClassDark:
      'bg-violet-600 text-white shadow-sm ring-1 ring-violet-500/40',
    cardClassLight:
      'border-violet-200/90 bg-gradient-to-br from-violet-50 via-violet-50/80 to-violet-100/60 shadow-md shadow-violet-200/30 ring-1 ring-violet-300/25',
    cardClassDark:
      'border-violet-600/50 bg-violet-950 shadow-md shadow-black/25 ring-1 ring-violet-500/25',
    footerDividerClassLight: 'border-violet-200/90',
    footerDividerClassDark: 'border-violet-700/50',
    labelAccentClassLight: 'text-violet-700',
    labelAccentClassDark: 'text-violet-300',
  },
  T3: {
    titleKey: 'pregnancy_modal_trimester_t3_title',
    rangeKey: 'pregnancy_modal_trimester_t3_range',
    chipClassLight:
      'bg-amber-600 text-white shadow-sm ring-1 ring-amber-500/35',
    chipClassDark:
      'bg-amber-600 text-white shadow-sm ring-1 ring-amber-500/40',
    cardClassLight:
      'border-amber-200/90 bg-gradient-to-br from-amber-50 via-amber-50/80 to-orange-50/70 shadow-md shadow-amber-200/35 ring-1 ring-amber-300/30',
    cardClassDark:
      'border-amber-600/50 bg-amber-950 shadow-md shadow-black/25 ring-1 ring-amber-500/25',
    footerDividerClassLight: 'border-amber-200/90',
    footerDividerClassDark: 'border-amber-700/50',
    labelAccentClassLight: 'text-amber-800',
    labelAccentClassDark: 'text-amber-200',
  },
};

const trimesterCardStyles = (trimester: Trimester, isDark: boolean) => {
  const meta = TRIMESTER_META[trimester];
  return {
    shell: isDark ? meta.cardClassDark : meta.cardClassLight,
    label: isDark ? meta.labelAccentClassDark : meta.labelAccentClassLight,
    footer: isDark ? meta.footerDividerClassDark : meta.footerDividerClassLight,
    chip: isDark ? meta.chipClassDark : meta.chipClassLight,
    title: isDark ? 'text-white' : 'text-title-page-color-100',
    subtitle: isDark ? 'text-gray-400' : 'text-title-page-color-300',
    footerText: isDark ? 'text-gray-400' : 'text-title-page-color-300',
    footerGa: isDark ? 'text-gray-100' : 'text-title-page-color-100',
  };
};
type PregnancyStatus =
  | 'Active'
  | 'Delivered'
  | 'Miscarried'
  | 'Terminated'
  | 'Unknown';

const BIO_LIMITS: Record<BioType, { min: number; max: number; unit: string }> =
  {
    CRL: { min: 5, max: 95, unit: 'mm' },
    BPD: { min: 2.0, max: 12.0, unit: 'cm' },
    HC: { min: 10.0, max: 40.0, unit: 'cm' },
    AC: { min: 10.0, max: 45.0, unit: 'cm' },
    FL: { min: 1.0, max: 9.0, unit: 'cm' },
  };

const PREGNANCY_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Miscarried', label: 'Miscarried' },
  { value: 'Terminated', label: 'Terminated' },
  { value: 'Unknown', label: 'Unknown' },
];

const PREGNANCY_RISK_OPTIONS = [
  { field: 'risque_anemie', labelKey: 'pregnancy_modal_risk_anemia' },
  { field: 'risque_hta', labelKey: 'pregnancy_modal_risk_hypertension' },
  { field: 'risque_diabete', labelKey: 'pregnancy_modal_risk_diabetes' },
  { field: 'risque_cardiopathie', labelKey: 'pregnancy_modal_risk_cardiopathy' },
  {
    field: 'risque_metrorragie',
    labelKey: 'pregnancy_modal_risk_metrorrhagia',
  },
  { field: 'risque_infection', labelKey: 'pregnancy_modal_risk_infection' },
  {
    field: 'risque_preeclampsie',
    labelKey: 'pregnancy_modal_risk_preeclampsia',
  },
  { field: 'risque_eclampsie', labelKey: 'pregnancy_modal_risk_eclampsia' },
] as const;

interface PregnancyFormState {
  lmp: string;
  edd: string;
  gaWeeks: string;
  gaDays: string;
  crl: string;
  bpd: string;
  hc: string;
  ac: string;
  fl: string;
  status?: PregnancyStatus;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string;
}

const initialPregnancyFormState: PregnancyFormState = {
  lmp: '',
  edd: '',
  gaWeeks: '',
  gaDays: '',
  crl: '',
  bpd: '',
  hc: '',
  ac: '',
  fl: '',
  status: 'Active',
  grossesse_classee_a_risque: null,
  risque_anemie: false,
  risque_hta: false,
  risque_diabete: false,
  risque_cardiopathie: false,
  risque_metrorragie: false,
  risque_infection: false,
  risque_preeclampsie: false,
  risque_eclampsie: false,
  risque_autre_details: '',
};

interface ParsedPatientName {
  family: string;
  given: string;
  middle: string;
  suffix: string;
  prefix: string;
  formatted: string;
  fullName: string;
}

const parsePatientName = (patientName: string): ParsedPatientName => {
  if (!patientName || patientName.trim() === '') {
    return {
      family: '',
      given: '',
      middle: '',
      suffix: '',
      prefix: '',
      formatted: 'Unknown Patient',
      fullName: 'Unknown Patient',
    };
  }

  const parts = patientName.split('^').map((p) => p.trim());

  const family = parts[0] || '';
  const given = parts[1] || '';
  const middle = parts[2] || '';
  const suffix = parts[3] || '';
  const prefix = parts[4] || '';

  let formatted = '';

  // Add prefix if exists (Dr, Ms, etc.)
  if (prefix) formatted += `${prefix} `;

  // Add given name
  if (given && given !== 'UNKNOWN') formatted += `${given} `;

  // Add middle name
  if (middle) formatted += `${middle} `;

  // Add family name
  if (family && family !== 'UNKNOWN') formatted += family;

  // Add suffix if exists (Jr, Sr, etc.)
  if (suffix) formatted += ` ${suffix}`;

  // Clean up
  formatted = formatted.trim();

  // Fallback if empty after parsing
  if (!formatted) {
    formatted = 'Unknown Patient';
  }

  // Build full name for display
  const fullName = formatted;

  return {
    family,
    given,
    middle,
    suffix,
    prefix,
    formatted,
    fullName,
  };
};

const calculateGA = (
  type: BioType,
  valueStr: string
): {
  totalDays: number;
  weeks: number;
  days: number;
  error?: string;
} | null => {
  const val = parseFloat(valueStr);
  const limit = BIO_LIMITS[type];
  if (!val || isNaN(val) || val < limit.min || val > limit.max)
    return { totalDays: 0, weeks: 0, days: 0, error: 'Invalid value' };

  let gaWeeks = 0;

  if (type === 'BPD') {
    gaWeeks = 9.54 + 1.482 * val + 0.1676 * (val * val);
  } else if (type === 'HC') {
    gaWeeks = 8.96 + 0.54 * val + 0.0003 * Math.pow(val, 3);
  } else if (type === 'AC') {
    gaWeeks = 8.14 + 0.753 * val + 0.0036 * (val * val);
  } else if (type === 'FL') {
    gaWeeks = 10.35 + 2.46 * val + 0.17 * (val * val);
  } else if (type === 'CRL') {
    const val_cm = val / 10.0;
    const exponent =
      1.684969 +
      0.315646 * val_cm -
      0.049306 * Math.pow(val_cm, 2) +
      0.004057 * Math.pow(val_cm, 3) -
      0.000120456 * Math.pow(val_cm, 4);
    gaWeeks = Math.exp(exponent);
  }

  const totalDays = gaWeeks * 7;
  const w = Math.floor(totalDays / 7);
  const d = Math.floor(totalDays % 7);

  if (w < 4 || w > 44)
    return { totalDays: 0, weeks: 0, days: 0, error: 'Out of range' };

  return { totalDays, weeks: w, days: d };
};

const formatGATag = (type: BioType, valueStr: string): string | null => {
  const result = calculateGA(type, valueStr);
  if (!result || result.error) return null;
  return `${result.weeks}w ${result.days}d`;
};

/** GA label from stored LMP for summary chips (worklist resolution). */
function gestationalAgeLabelFromLmp(
  lmpRaw: string | null | undefined
): string | null {
  if (!lmpRaw) return null;
  const lmp = dayjs(lmpRaw);
  if (!lmp.isValid()) return null;
  const ref = dayjs().startOf('day');
  const d = ref.diff(lmp.startOf('day'), 'day');
  if (d < 0) return null;
  return `${Math.floor(d / 7)}+${d % 7}`;
}

interface BiometryInputProps {
  type: BioType;
  value: string;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

const bioInputClass = (isError: boolean, disabled: boolean) =>
  [
    'w-full rounded-lg border px-3 py-2.5 pr-[4.25rem] text-sm tabular-nums transition-shadow',
    'bg-card-color-100 text-title-page-color-100 placeholder:text-title-page-color-300/70',
    'border-slate-200 dark:border-slate-600 dark:bg-gray-800/80 dark:text-gray-100',
    'focus:border-primary-dui/50 focus:outline-none focus:ring-2 focus:ring-primary-dui/25',
    disabled && 'cursor-not-allowed opacity-50',
    isError &&
      'border-red-400 bg-red-50/80 text-red-900 focus:ring-red-200 dark:border-red-500/60 dark:bg-red-950/30 dark:text-red-200',
  ]
    .filter(Boolean)
    .join(' ');

const BiometryInput: React.FC<BiometryInputProps> = ({
  type,
  value,
  onChange,
  disabled = false,
}) => {
  const { t: tP } = useTranslation('Patient');
  const gaTag = formatGATag(type, value);
  const limit = BIO_LIMITS[type];
  const isError =
    value && (parseFloat(value) < limit.min || parseFloat(value) > limit.max);

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={type.toLowerCase()}
        className="flex items-baseline justify-between gap-2 text-xs font-medium text-title-page-color-300 dark:text-gray-400"
      >
        <span className="text-title-page-color-100 dark:text-gray-200">
          {type}
        </span>
        <span className="text-[10px] font-normal uppercase tracking-wide text-title-page-color-300/80 dark:text-gray-500">
          {limit.unit} · {limit.min}–{limit.max}
        </span>
      </label>
      <div className="relative">
        <input
          type="number"
          step="0.1"
          id={type.toLowerCase()}
          placeholder={tP('pregnancy_modal_bio_placeholder', {
            value: limit.min,
          })}
          value={value}
          onChange={(e) => onChange(type.toLowerCase(), e.target.value)}
          disabled={disabled}
          className={bioInputClass(!!isError, disabled)}
        />
        {gaTag && !isError && (
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-primary-dui/10 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light">
            ~{gaTag}
          </span>
        )}
      </div>
    </div>
  );
};

interface PregnancyModalProps {
  isOpen: boolean;
  onClose: () => void;
  worklistItem: EnrichedWorklistItem | null;
  onSaveLocal?: (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => void;
  onUpdateSuccess?: (updatedPregnancy: any) => void;
  onSaveAndConfirm?: (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => void;
  initialData?: Partial<PregnancyFormState & { trimester: Trimester }>;
  mode: 'create' | 'edit-local' | 'edit-backend';
  pregnancyId?: number;
  /** Exam / DICOM study date (YYYY-MM-DD) when no stored ultrasound date. */
  studyDate?: string;
  /** Prefer this as the reference date for LMP→GA math (e.g. last ultrasound visit). */
  ultrasoundDate?: string;
  onRequestClosePregnancy?: (status: PregnancyStatus) => void;
}

export const PregnancyModal: React.FC<PregnancyModalProps> = ({
  isOpen,
  onClose,
  worklistItem,
  onSaveLocal,
  onUpdateSuccess,
  onSaveAndConfirm,
  initialData,
  mode,
  pregnancyId,
  studyDate,
  ultrasoundDate,
  onRequestClosePregnancy,
}) => {
  const { t: tP } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');
  const isDarkTheme = useHtmlDataTheme() === 'dark';

  const PREGNANCY_STATUS_LABEL_KEYS: Record<PregnancyStatus, string> = {
    Active: 'pregnancy_modal_pregnancy_status_active',
    Delivered: 'pregnancy_modal_pregnancy_status_delivered',
    Miscarried: 'pregnancy_modal_pregnancy_status_miscarried',
    Terminated: 'pregnancy_modal_pregnancy_status_terminated',
    Unknown: 'pregnancy_modal_pregnancy_status_unknown',
  };

  const [pregnancyFormValues, setPregnancyFormValues] =
    useState<PregnancyFormState>(initialPregnancyFormState);
  const [isEddPassed, setIsEddPassed] = useState(false);

  const gaReferenceDateStr = useMemo(
    () => ultrasoundDate || studyDate,
    [ultrasoundDate, studyDate]
  );

  const computedGA = useMemo(
    () => computeGAFromLmp(pregnancyFormValues.lmp, gaReferenceDateStr),
    [pregnancyFormValues.lmp, gaReferenceDateStr]
  );

  const trimester: Trimester = useMemo(() => {
    if (!computedGA) return 'T1';
    if (computedGA.weeks >= 28) return 'T3';
    if (computedGA.weeks >= 14) return 'T2';
    return 'T1';
  }, [computedGA]);

  const trimesterMeta = TRIMESTER_META[trimester];
  const trimesterStyles = trimesterCardStyles(trimester, isDarkTheme);

  const hasCRL =
    !!pregnancyFormValues.crl && parseFloat(pregnancyFormValues.crl) > 0;
  const hasOthers =
    (!!pregnancyFormValues.bpd && parseFloat(pregnancyFormValues.bpd) > 0) ||
    (!!pregnancyFormValues.hc && parseFloat(pregnancyFormValues.hc) > 0) ||
    (!!pregnancyFormValues.ac && parseFloat(pregnancyFormValues.ac) > 0) ||
    (!!pregnancyFormValues.fl && parseFloat(pregnancyFormValues.fl) > 0);

  const disableCRL = hasOthers;
  const disableOthers = hasCRL;

  const [pendingSaveAndConfirm, setPendingSaveAndConfirm] = useState(false);

  const { mutate: updatePregnancy, isPending: isUpdatingPregnancy } =
    useUpdatePregnancy(
      (response, data) => {
        const updatedPregnancy = response?.data || data;

        if (pendingSaveAndConfirm && onSaveAndConfirm) {
          message.success(tPM('modals.pregnancy.success.updateSavingViews'));

          const pregnancyDataForViews = {
            ...pregnancyFormValues,
            trimester,
            existsInBackend: true,
            id: updatedPregnancy?.id || pregnancyId,
            patient_id: worklistItem?.resolution?.patient_uuid || '',
          };

          onSaveAndConfirm(pregnancyDataForViews);
          setPendingSaveAndConfirm(false);
          setPregnancyFormValues(initialPregnancyFormState);
        } else {
          message.success(tPM('modals.pregnancy.success.update'));

          if (onUpdateSuccess && updatedPregnancy) {
            onUpdateSuccess(updatedPregnancy);
          }

          onClose();
          setPregnancyFormValues(initialPregnancyFormState);
        }
      },
      (error) => {
        message.error(tPM('modals.pregnancy.error.update'));
        setPendingSaveAndConfirm(false);
      }
    );

  useEffect(() => {
    if (isOpen && initialData) {
      const resolvedLmp =
        initialData.lmp ||
        (initialData.edd
          ? dayjs(initialData.edd).subtract(280, 'day').format('YYYY-MM-DD')
          : '');

      let resolvedWeeks = initialData.gaWeeks || '';
      let resolvedDays = initialData.gaDays || '';
      if ((!resolvedWeeks || resolvedWeeks === '0') && resolvedLmp) {
        const ref = (
          gaReferenceDateStr ? dayjs(gaReferenceDateStr) : dayjs()
        ).startOf('day');
        const diffDays = ref.diff(dayjs(resolvedLmp).startOf('day'), 'day');
        if (diffDays >= 0) {
          resolvedWeeks = Math.floor(diffDays / 7).toString();
          resolvedDays = (diffDays % 7).toString();
        }
      }

      setPregnancyFormValues({
        lmp: resolvedLmp,
        edd: initialData.edd || '',
        gaWeeks: resolvedWeeks,
        gaDays: resolvedDays,
        crl: initialData.crl || '',
        bpd: initialData.bpd || '',
        hc: initialData.hc || '',
        ac: initialData.ac || '',
        fl: initialData.fl || '',
        status: initialData.status || 'Active',
        grossesse_classee_a_risque:
          typeof initialData.grossesse_classee_a_risque === 'boolean'
            ? initialData.grossesse_classee_a_risque
            : null,
        risque_anemie: initialData.risque_anemie === true,
        risque_hta: initialData.risque_hta === true,
        risque_diabete: initialData.risque_diabete === true,
        risque_cardiopathie: initialData.risque_cardiopathie === true,
        risque_metrorragie: initialData.risque_metrorragie === true,
        risque_infection: initialData.risque_infection === true,
        risque_preeclampsie: initialData.risque_preeclampsie === true,
        risque_eclampsie: initialData.risque_eclampsie === true,
        risque_autre_details: initialData.risque_autre_details || '',
      });

      if (mode === 'edit-backend' && initialData.edd) {
        const eddDate = dayjs(initialData.edd);
        setIsEddPassed(dayjs().isAfter(eddDate, 'day'));
      } else {
        setIsEddPassed(false);
      }
    } else if (isOpen && !initialData) {
      setPregnancyFormValues(initialPregnancyFormState);
      setIsEddPassed(false);
    }
  }, [isOpen, initialData, mode, gaReferenceDateStr]);

  useEffect(() => {
    if (!pregnancyFormValues.lmp) return;
    const lmp = dayjs(pregnancyFormValues.lmp).startOf('day');
    const newEdd = lmp.add(280, 'day');
    const referenceDate = (
      gaReferenceDateStr ? dayjs(gaReferenceDateStr) : dayjs()
    ).startOf('day');
    const diffDays = referenceDate.diff(lmp, 'day');
    const w = Math.floor(diffDays / 7);
    const d = diffDays % 7;
    if (!dayjs(pregnancyFormValues.edd).isSame(newEdd, 'day')) {
      setPregnancyFormValues((prev) => ({
        ...prev,
        edd: newEdd.format('YYYY-MM-DD'),
        gaWeeks: w >= 0 ? w.toString() : '0',
        gaDays: d >= 0 ? d.toString() : '0',
      }));
    }
  }, [pregnancyFormValues.lmp, gaReferenceDateStr]);

  useEffect(() => {
    if (!pregnancyFormValues.edd) return;
    const expectedLmp = dayjs(pregnancyFormValues.edd)
      .startOf('day')
      .subtract(280, 'day');
    const referenceDate = (
      gaReferenceDateStr ? dayjs(gaReferenceDateStr) : dayjs()
    ).startOf('day');
    const diffDays = referenceDate.diff(expectedLmp, 'day');
    const w = Math.floor(diffDays / 7);
    const d = diffDays % 7;
    if (!dayjs(pregnancyFormValues.lmp).isSame(expectedLmp, 'day')) {
      setPregnancyFormValues((prev) => ({
        ...prev,
        lmp: expectedLmp.format('YYYY-MM-DD'),
        gaWeeks: w >= 0 ? w.toString() : '0',
        gaDays: d >= 0 ? d.toString() : '0',
      }));
    }
  }, [pregnancyFormValues.edd, gaReferenceDateStr]);

  useEffect(() => {
    const w = parseInt(pregnancyFormValues.gaWeeks || '0');
    const d = parseInt(pregnancyFormValues.gaDays || '0');
    const totalDays = w * 7 + d;
    if (totalDays <= 0) return;
    const referenceDate = (
      gaReferenceDateStr ? dayjs(gaReferenceDateStr) : dayjs()
    ).startOf('day');
    const calculatedLmp = referenceDate.subtract(totalDays, 'day');
    if (!dayjs(pregnancyFormValues.lmp).isSame(calculatedLmp, 'day')) {
      setPregnancyFormValues((prev) => ({
        ...prev,
        lmp: calculatedLmp.format('YYYY-MM-DD'),
      }));
    }
  }, [
    pregnancyFormValues.gaWeeks,
    pregnancyFormValues.gaDays,
    gaReferenceDateStr,
  ]);

  useEffect(() => {
    if (mode === 'edit-backend' && pregnancyFormValues.edd) {
      const eddDate = dayjs(pregnancyFormValues.edd);
      const today = dayjs();
      setIsEddPassed(today.isAfter(eddDate, 'day'));
    }
  }, [pregnancyFormValues.edd, mode]);

  const handlePregnancyInputChange = (field: string, value: any) => {
    setPregnancyFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRiskClassificationChange = (value: boolean | null) => {
    setPregnancyFormValues((prev) => ({
      ...prev,
      grossesse_classee_a_risque: value,
      ...(value === true
        ? {}
        : {
            risque_anemie: false,
            risque_hta: false,
            risque_diabete: false,
            risque_cardiopathie: false,
            risque_metrorragie: false,
            risque_infection: false,
            risque_preeclampsie: false,
            risque_eclampsie: false,
            risque_autre_details: '',
          }),
    }));
  };

  const handleBiometryChange = (type: BioType, value: string) => {
    setPregnancyFormValues((prev) => {
      const newForm = { ...prev };

      if (type === 'CRL') newForm.crl = value;
      else if (type === 'BPD') newForm.bpd = value;
      else if (type === 'HC') newForm.hc = value;
      else if (type === 'AC') newForm.ac = value;
      else if (type === 'FL') newForm.fl = value;

      const allBiometry = [
        newForm.crl,
        newForm.bpd,
        newForm.hc,
        newForm.ac,
        newForm.fl,
      ];
      const hasValues = allBiometry.some((val) => val !== '' && val !== null);

      if (!hasValues) {
        return { ...newForm, gaWeeks: '', gaDays: '', lmp: '', edd: '' };
      }

      let calculatedTotalDays = 0;

      if (type === 'CRL' && value) {
        const res = calculateGA('CRL', value);
        if (res && !res.error) calculatedTotalDays = res.totalDays;
      } else {
        const otherFields: ('BPD' | 'HC' | 'AC' | 'FL')[] = [
          'BPD',
          'HC',
          'AC',
          'FL',
        ];
        let sumDays = 0;
        let count = 0;
        otherFields.forEach((field) => {
          const val =
            field === type ? value : (newForm as any)[field.toLowerCase()];
          if (val) {
            const res = calculateGA(field, val);
            if (res && !res.error) {
              sumDays += res.totalDays;
              count++;
            }
          }
        });
        if (count > 0) calculatedTotalDays = sumDays / count;
      }

      if (calculatedTotalDays > 0) {
        const w = Math.floor(calculatedTotalDays / 7);
        const d = Math.floor(calculatedTotalDays % 7);
        newForm.gaWeeks = w.toString();
        newForm.gaDays = d.toString();
        const referenceDate = (
          gaReferenceDateStr ? dayjs(gaReferenceDateStr) : dayjs()
        ).startOf('day');
        const calculatedLmp = referenceDate.subtract(
          Math.round(calculatedTotalDays),
          'day'
        );
        newForm.lmp = calculatedLmp.format('YYYY-MM-DD');
        newForm.edd = calculatedLmp.add(280, 'day').format('YYYY-MM-DD');
      }

      return newForm;
    });
  };

  const handleSave = () => {
    if (!worklistItem) return;

    const mwl = worklistItem.mwl;
    const patientId = mwl.PatientID || '';

    if (
      !pregnancyFormValues.lmp &&
      !pregnancyFormValues.edd &&
      !pregnancyFormValues.gaWeeks
    ) {
      message.warning(tPM('modals.pregnancy.warnings.missingDating'));
      return;
    }

    if (mode === 'create' || mode === 'edit-local') {
      if (onSaveLocal) {
        onSaveLocal({
          ...pregnancyFormValues,
          trimester,
        });
      }
      onClose();
      setPregnancyFormValues(initialPregnancyFormState);
      return;
    }

    if (mode === 'edit-backend' && pregnancyId) {
      if (
        onRequestClosePregnancy &&
        pregnancyFormValues.status &&
        pregnancyFormValues.status !== 'Active'
      ) {
        onRequestClosePregnancy(pregnancyFormValues.status);
        return;
      }

      const toISO = (dateStr: string | undefined) => {
        if (!dateStr) return undefined;
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? undefined : d.toISOString();
      };

      const updateData: PregnancyData & { id: number } = {
        id: Number(pregnancyId),
        patient_id: worklistItem.resolution.patient_uuid || '',
        last_menstrual_period: toISO(pregnancyFormValues.lmp),
        estimated_due_date: toISO(pregnancyFormValues.edd),
        study_date: toISO(studyDate),
        ultrasound_date: toISO(gaReferenceDateStr),
        pregnancy_status: pregnancyFormValues.status,
        grossesse_classee_a_risque:
          pregnancyFormValues.grossesse_classee_a_risque,
        risque_anemie: pregnancyFormValues.risque_anemie === true,
        risque_hta: pregnancyFormValues.risque_hta === true,
        risque_diabete: pregnancyFormValues.risque_diabete === true,
        risque_cardiopathie: pregnancyFormValues.risque_cardiopathie === true,
        risque_metrorragie: pregnancyFormValues.risque_metrorragie === true,
        risque_infection: pregnancyFormValues.risque_infection === true,
        risque_preeclampsie: pregnancyFormValues.risque_preeclampsie === true,
        risque_eclampsie: pregnancyFormValues.risque_eclampsie === true,
        risque_autre_details:
          pregnancyFormValues.risque_autre_details?.trim() || null,
        preg_bpd: pregnancyFormValues.bpd
          ? parseFloat(pregnancyFormValues.bpd)
          : undefined,
        preg_hc: pregnancyFormValues.hc
          ? parseFloat(pregnancyFormValues.hc)
          : undefined,
        preg_ac: pregnancyFormValues.ac
          ? parseFloat(pregnancyFormValues.ac)
          : undefined,
        preg_fl: pregnancyFormValues.fl
          ? parseFloat(pregnancyFormValues.fl)
          : undefined,
        preg_crl: pregnancyFormValues.crl
          ? parseFloat(pregnancyFormValues.crl)
          : undefined,
      };

      updatePregnancy(updateData);
    }
  };

  const handleSaveAndConfirm = () => {
    if (!worklistItem) return;

    if (
      !pregnancyFormValues.lmp &&
      !pregnancyFormValues.edd &&
      !pregnancyFormValues.gaWeeks
    ) {
      message.warning(tPM('modals.pregnancy.warnings.missingDating'));
      return;
    }

    if (mode === 'edit-backend' && pregnancyId) {
      setPendingSaveAndConfirm(true);

      const toISO = (dateStr: string | undefined) => {
        if (!dateStr) return undefined;
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? undefined : d.toISOString();
      };

      const updateData: PregnancyData & { id: number } = {
        id: Number(pregnancyId),
        patient_id: worklistItem.resolution?.patient_uuid || '',
        last_menstrual_period: toISO(pregnancyFormValues.lmp),
        estimated_due_date: toISO(pregnancyFormValues.edd),
        study_date: toISO(studyDate),
        ultrasound_date: toISO(gaReferenceDateStr),
        pregnancy_status: pregnancyFormValues.status,
        grossesse_classee_a_risque:
          pregnancyFormValues.grossesse_classee_a_risque,
        risque_anemie: pregnancyFormValues.risque_anemie === true,
        risque_hta: pregnancyFormValues.risque_hta === true,
        risque_diabete: pregnancyFormValues.risque_diabete === true,
        risque_cardiopathie: pregnancyFormValues.risque_cardiopathie === true,
        risque_metrorragie: pregnancyFormValues.risque_metrorragie === true,
        risque_infection: pregnancyFormValues.risque_infection === true,
        risque_preeclampsie: pregnancyFormValues.risque_preeclampsie === true,
        risque_eclampsie: pregnancyFormValues.risque_eclampsie === true,
        risque_autre_details:
          pregnancyFormValues.risque_autre_details?.trim() || null,
        preg_bpd: pregnancyFormValues.bpd
          ? parseFloat(pregnancyFormValues.bpd)
          : undefined,
        preg_hc: pregnancyFormValues.hc
          ? parseFloat(pregnancyFormValues.hc)
          : undefined,
        preg_ac: pregnancyFormValues.ac
          ? parseFloat(pregnancyFormValues.ac)
          : undefined,
        preg_fl: pregnancyFormValues.fl
          ? parseFloat(pregnancyFormValues.fl)
          : undefined,
        preg_crl: pregnancyFormValues.crl
          ? parseFloat(pregnancyFormValues.crl)
          : undefined,
      };

      updatePregnancy(updateData);
      return;
    }

    const pregnancyDataToSave = {
      ...pregnancyFormValues,
      trimester,
      existsInBackend: false,
    };

    if (onSaveAndConfirm) {
      onSaveAndConfirm(pregnancyDataToSave);
      setPregnancyFormValues(initialPregnancyFormState);
    } else {
      handleSave();
    }
  };

  if (!isOpen || !worklistItem) return null;

  const mwl = worklistItem.mwl;
  const parsedName = parsePatientName(mwl.PatientName);
  const resolution = worklistItem.resolution;
  const existingOnFile = resolution?.pregnancy;
  const showExistingActivePregnancyCallout =
    (mode === 'create' || mode === 'edit-local') &&
    Boolean(resolution?.active_pregnancy_exists && existingOnFile);

  const referenceDate = gaReferenceDateStr
    ? dayjs(gaReferenceDateStr)
    : dayjs();
  const maxLmpDate = referenceDate.format('YYYY-MM-DD');
  const minLmpDate = referenceDate.subtract(45, 'week').format('YYYY-MM-DD');
  const maxEddDate = referenceDate.add(45, 'week').format('YYYY-MM-DD');
  const minEddDate = referenceDate.format('YYYY-MM-DD');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <Modal.Header
        title={
          mode === 'edit-backend'
            ? tPM('modals.pregnancy.titleEdit')
            : tPM('modals.pregnancy.titleCreate')
        }
        onClose={onClose}
        description={
          <div className="space-y-2.5">
            <div className="inline-flex items-center gap-2 rounded-lg bg-primary-dui/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light">
              <FiActivity size={13} aria-hidden />
              {tP('pregnancy_modal_obstetric_dating')}
            </div>
            <p className="text-sm text-title-page-color-300 dark:text-gray-400">
              <span className="font-semibold text-title-page-color-100 dark:text-white">
                {parsedName.fullName === 'Unknown Patient'
                  ? tP('pregnancy_modal_unknown_patient')
                  : parsedName.fullName}
              </span>
              <span className="mx-2 text-title-page-color-300/50 dark:text-gray-600">
                ·
              </span>
              <span className="font-mono text-xs">
                {tP('pregnancy_modal_id_prefix', { id: mwl.PatientID })}
              </span>
            </p>
          </div>
        }
      />

      <Modal.Body className="max-h-[min(60vh,480px)] sm:max-h-[min(68vh,580px)]">
        <div className="space-y-5">
          {showExistingActivePregnancyCallout && existingOnFile ? (
            <div
              className={
                'relative overflow-hidden rounded-xl border p-4 shadow-lg ring-1 ' +
                (isDarkTheme
                  ? 'border-primary-dui/40 bg-gray-900 shadow-black/20 ring-primary-dui/25'
                  : 'border-primary-dui/30 bg-gradient-to-br from-primary-dui/[0.08] via-card-color-100 to-violet-500/[0.07] shadow-primary-dui/10 ring-primary-dui/15')
              }
              role="status"
              aria-live="polite"
            >
              <div
                className={
                  'pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full blur-3xl ' +
                  (isDarkTheme ? 'bg-primary-dui/20' : 'bg-primary-dui/15')
                }
                aria-hidden
              />
              <div className="relative flex gap-4">
                <div className="flex shrink-0 flex-col items-center gap-2 pt-1">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-dui/50 opacity-60" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-primary-dui shadow-sm dark:bg-primary-light" />
                  </span>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-dui/15 text-primary-dui shadow-inner dark:bg-primary-dui/25 dark:text-primary-light">
                    <FiInfo size={22} strokeWidth={2.25} aria-hidden />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold tracking-tight text-title-page-color-100 dark:text-white">
                    {tP('pregnancy_modal_active_already_title')}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-title-page-color-300 dark:text-gray-400">
                    {tP('pregnancy_modal_active_already_body_before')}{' '}
                    <span className="font-semibold text-primary-dui dark:text-primary-light">
                      {tP('pregnancy_modal_active_already_body_active')}
                    </span>{' '}
                    {tP('pregnancy_modal_active_already_body_middle')}{' '}
                    <strong className="font-semibold text-title-page-color-100 dark:text-gray-200">
                      {tP('pregnancy_modal_active_already_body_action')}
                    </strong>{' '}
                    {tP('pregnancy_modal_active_already_body_after')}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {existingOnFile.id != null ? (
                      <span className="inline-flex items-center rounded-lg bg-white/80 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-title-page-color-100 shadow-sm ring-1 ring-slate-200/80 dark:bg-gray-800/90 dark:text-gray-100 dark:ring-slate-600">
                        {tP('pregnancy_modal_active_already_pregnancy_id', {
                          id: existingOnFile.id,
                        })}
                      </span>
                    ) : null}
                    {existingOnFile.estimated_due_date ? (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-white/80 px-2.5 py-1 text-[11px] font-medium text-title-page-color-100 shadow-sm ring-1 ring-slate-200/80 dark:bg-gray-800/90 dark:text-gray-100 dark:ring-slate-600">
                        <FiCalendar
                          className="text-primary-dui dark:text-primary-light"
                          size={12}
                          aria-hidden
                        />
                        EDD{' '}
                        {dayjs(existingOnFile.estimated_due_date).format(
                          'MMM D, YYYY'
                        )}
                      </span>
                    ) : null}
                    {gestationalAgeLabelFromLmp(
                      existingOnFile.last_menstrual_period
                    ) ? (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-white/80 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-title-page-color-100 shadow-sm ring-1 ring-slate-200/80 dark:bg-gray-800/90 dark:text-gray-100 dark:ring-slate-600">
                        <FiActivity
                          className="text-primary-dui dark:text-primary-light"
                          size={12}
                          aria-hidden
                        />
                        GA{' '}
                        {gestationalAgeLabelFromLmp(
                          existingOnFile.last_menstrual_period
                        )}
                      </span>
                    ) : null}
                    {existingOnFile.is_medically_active === false ? (
                      <span
                        className={
                          'inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-semibold ' +
                          (isDarkTheme
                            ? 'bg-amber-900 text-amber-100'
                            : 'bg-amber-500/15 text-amber-900')
                        }
                      >
                        {tP(
                          'pregnancy_modal_active_already_not_medically_active'
                        )}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {isEddPassed && mode === 'edit-backend' && (
            <div
              className={amberWarningAlertClass(isDarkTheme, 'md')}
              role="status"
            >
              <FiAlertTriangle
                className={
                  'mt-0.5 shrink-0 ' +
                  (isDarkTheme ? 'text-amber-400' : 'text-amber-600')
                }
                size={18}
              />
              <div>
                <p
                  className={
                    'text-sm font-semibold ' +
                    (isDarkTheme ? 'text-amber-100' : 'text-amber-950')
                  }
                >
                  {tP('pregnancy_modal_edd_passed_title')}
                </p>
                <p
                  className={
                    'mt-1 text-xs leading-relaxed ' +
                    (isDarkTheme ? 'text-amber-200' : 'text-amber-900/85')
                  }
                >
                  {tP('pregnancy_modal_edd_passed_body', {
                    date: dayjs(pregnancyFormValues.edd).format('DD MMM YYYY'),
                  })}
                </p>
              </div>
            </div>
          )}

          <div
            className={`grid gap-3 ${mode === 'edit-backend' ? 'sm:grid-cols-2' : 'grid-cols-1'}`}
          >
            {mode === 'edit-backend' && (
              <div className="rounded-xl border border-slate-200 bg-card-color-100 p-3.5 shadow-sm dark:border-slate-600 dark:bg-gray-800/60">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-500">
                  {tP('pregnancy_modal_status_label')}
                </p>
                <Select
                  id="pregnancy_status"
                  name="pregnancy_status"
                  value={pregnancyFormValues.status || 'Active'}
                  onChange={(e) =>
                    handlePregnancyInputChange('status', e.target.value)
                  }
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-title-page-color-100 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                >
                  {PREGNANCY_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {tP(
                        PREGNANCY_STATUS_LABEL_KEYS[
                          option.value as PregnancyStatus
                        ]
                      )}
                    </option>
                  ))}
                </Select>
                {onRequestClosePregnancy &&
                  pregnancyFormValues.status &&
                  pregnancyFormValues.status !== 'Active' && (
                    <div
                      className={`mt-2 ${amberWarningAlertClass(isDarkTheme, 'sm')}`}
                      role="status"
                    >
                      <FiAlertTriangle
                        className={
                          'mt-0.5 shrink-0 ' +
                          (isDarkTheme ? 'text-amber-400' : 'text-amber-600')
                        }
                        size={14}
                      />
                      <span>
                        {pregnancyFormValues.status === 'Unknown'
                          ? tP('pregnancy_modal_status_warning_unknown')
                          : pregnancyFormValues.status === 'Miscarried'
                            ? tP('pregnancy_modal_status_warning_miscarried')
                            : tP('pregnancy_modal_status_warning_other')}
                      </span>
                    </div>
                  )}
              </div>
            )}

            <div
              className={`rounded-xl border p-3.5 ${trimesterStyles.shell}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p
                    className={`text-[10px] font-semibold uppercase tracking-wider ${trimesterStyles.label}`}
                  >
                    {tP('pregnancy_modal_trimester_label')}
                  </p>
                  <p
                    className={`mt-1 text-sm font-semibold ${trimesterStyles.title}`}
                  >
                    {tP(trimesterMeta.titleKey)}
                  </p>
                  <p className={`mt-0.5 text-[11px] ${trimesterStyles.subtitle}`}>
                    {tP(trimesterMeta.rangeKey)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold tabular-nums ${trimesterStyles.chip}`}
                >
                  {trimester}
                </span>
              </div>
              {computedGA ? (
                <p
                  className={`mt-2 border-t pt-2 text-[11px] ${trimesterStyles.footerText} ${trimesterStyles.footer}`}
                >
                  {tP('pregnancy_modal_ga_from_lmp_ref', {
                    date: gaReferenceDateStr
                      ? dayjs(gaReferenceDateStr).format('MMM D, YYYY')
                      : tP('pregnancy_modal_ga_from_lmp_today'),
                  })}{' '}
                  <span
                    className={`font-semibold tabular-nums ${trimesterStyles.footerGa}`}
                  >
                    {computedGA.weeks}+{computedGA.days}
                  </span>
                </p>
              ) : null}
            </div>
          </div>

          <section className="rounded-xl border border-slate-200 bg-card-color-100 p-4 shadow-sm dark:border-slate-600 dark:bg-gray-800/50">
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-400">
              <FiCalendar className="text-primary-dui" size={14} />
              {tP('pregnancy_modal_dating_title')}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label={tP('pregnancy_modal_lmp_label')}
                icon={FiCalendar}
              >
                <DateInput
                  id="lmp"
                  name="lmp"
                  value={pregnancyFormValues.lmp}
                  onChange={(e) =>
                    handlePregnancyInputChange('lmp', e.target.value)
                  }
                  min={minLmpDate}
                  max={maxLmpDate}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-title-page-color-100 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                />
              </FormField>
              <FormField
                label={tP('pregnancy_modal_edd_label')}
                icon={FiCalendar}
              >
                <DateInput
                  id="edd"
                  name="edd"
                  value={pregnancyFormValues.edd}
                  onChange={(e) =>
                    handlePregnancyInputChange('edd', e.target.value)
                  }
                  min={minEddDate}
                  max={maxEddDate}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-title-page-color-100 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                />
              </FormField>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 dark:border-slate-600/80">
              <FormField
                label={tP('pregnancy_modal_weeks_label')}
                htmlFor="gaWeeks"
              >
                <input
                  id="gaWeeks"
                  name="gaWeeks"
                  type="number"
                  min={0}
                  max={45}
                  placeholder={tP('pregnancy_modal_weeks_placeholder')}
                  value={pregnancyFormValues.gaWeeks}
                  onChange={(e) =>
                    handlePregnancyInputChange('gaWeeks', e.target.value)
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm tabular-nums text-title-page-color-100 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                />
              </FormField>
              <FormField
                label={tP('pregnancy_modal_days_label')}
                htmlFor="gaDays"
              >
                <input
                  id="gaDays"
                  name="gaDays"
                  type="number"
                  min={0}
                  max={6}
                  placeholder={tP('pregnancy_modal_days_placeholder')}
                  value={pregnancyFormValues.gaDays}
                  onChange={(e) =>
                    handlePregnancyInputChange('gaDays', e.target.value)
                  }
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm tabular-nums text-title-page-color-100 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                />
              </FormField>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-card-color-100 p-4 shadow-sm dark:border-slate-600 dark:bg-gray-800/50">
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-400">
              <FiAlertTriangle className="text-primary-dui" size={14} />
              {tP('pregnancy_modal_high_risk_title')}
            </h3>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
                  {tP('pregnancy_modal_high_risk_label')}
                </p>
                <p className="mt-1 text-xs text-title-page-color-300 dark:text-gray-400">
                  {tP('pregnancy_modal_high_risk_helper')}
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-600 dark:bg-gray-900">
                {[
                  { value: true, label: tP('yes') },
                  { value: false, label: tP('no') },
                  {
                    value: null,
                    label: tPM('timeline.currentPregnancy.notDocumented'),
                  },
                ].map(({ value, label }) => {
                  const selected =
                    pregnancyFormValues.grossesse_classee_a_risque === value;
                  return (
                    <button
                      key={String(value)}
                      type="button"
                      onClick={() => handleRiskClassificationChange(value)}
                      className={
                        'rounded-md px-4 py-1.5 text-sm font-medium transition-colors ' +
                        (selected
                          ? 'bg-primary-dui text-white shadow-sm'
                          : 'text-title-page-color-300 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-800')
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {pregnancyFormValues.grossesse_classee_a_risque === true && (
              <div className="mt-4 space-y-4 border-t border-slate-100 pt-4 dark:border-slate-600/80">
                <p className="text-xs font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-400">
                  {tP('pregnancy_modal_risk_types')}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {PREGNANCY_RISK_OPTIONS.map((option) => (
                    <label
                      key={option.field}
                      className="flex cursor-pointer select-none items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-title-page-color-100 transition-colors hover:border-primary-dui/50 dark:border-slate-600 dark:bg-gray-900 dark:text-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={pregnancyFormValues[option.field] === true}
                        onChange={(event) =>
                          handlePregnancyInputChange(
                            option.field,
                            event.target.checked
                          )
                        }
                        className="h-4 w-4 accent-primary-dui"
                      />
                      {tP(option.labelKey)}
                    </label>
                  ))}
                </div>
                <FormField
                  label={tP('pregnancy_modal_risk_other')}
                  htmlFor="risque_autre_details"
                >
                  <textarea
                    id="risque_autre_details"
                    value={pregnancyFormValues.risque_autre_details || ''}
                    placeholder={tP('pregnancy_modal_risk_other_placeholder')}
                    onChange={(event) =>
                      handlePregnancyInputChange(
                        'risque_autre_details',
                        event.target.value
                      )
                    }
                    className="min-h-20 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-title-page-color-100 placeholder:text-title-page-color-300/70 focus:border-primary-dui/40 focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-600 dark:bg-gray-900 dark:text-white"
                  />
                </FormField>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-slate-200 bg-card-color-100 p-4 shadow-sm dark:border-slate-600 dark:bg-gray-800/50">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-400">
                <FiActivity className="text-primary-dui" size={14} />
                {tP('pregnancy_modal_biometry')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {disableOthers && (
                  <span
                    className={
                      'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ' +
                      (isDarkTheme
                        ? 'bg-amber-900 text-amber-200'
                        : 'bg-amber-500/15 text-amber-800')
                    }
                  >
                    <FiLock size={10} aria-hidden />
                    {tP('pregnancy_modal_crl_mode')}
                  </span>
                )}
                {disableCRL && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-dui/10 px-2.5 py-1 text-[10px] font-semibold text-primary-dui dark:text-primary-light">
                    <FiLock size={10} aria-hidden />
                    {tP('pregnancy_modal_multi_param')}
                  </span>
                )}
              </div>
            </div>

            <div
              className={
                disableCRL
                  ? 'pointer-events-none mb-4 rounded-lg opacity-50 grayscale'
                  : 'mb-4'
              }
            >
              <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-title-page-color-300 dark:text-gray-500">
                {tP('pregnancy_modal_first_trimester_short')}
              </p>
              <BiometryInput
                type="CRL"
                value={pregnancyFormValues.crl}
                onChange={(field, value) => handleBiometryChange('CRL', value)}
                disabled={disableCRL}
              />
            </div>

            <div>
              <p className="mb-3 text-[10px] font-medium uppercase tracking-wide text-title-page-color-300 dark:text-gray-500">
                {tP('pregnancy_modal_second_third')}
              </p>
              <div
                className={`grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 ${disableOthers ? 'pointer-events-none opacity-50 grayscale' : ''}`}
              >
                <BiometryInput
                  type="BPD"
                  value={pregnancyFormValues.bpd}
                  onChange={(field, value) =>
                    handleBiometryChange('BPD', value)
                  }
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="HC"
                  value={pregnancyFormValues.hc}
                  onChange={(field, value) => handleBiometryChange('HC', value)}
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="AC"
                  value={pregnancyFormValues.ac}
                  onChange={(field, value) => handleBiometryChange('AC', value)}
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="FL"
                  value={pregnancyFormValues.fl}
                  onChange={(field, value) => handleBiometryChange('FL', value)}
                  disabled={disableOthers}
                />
              </div>
            </div>
          </section>
        </div>
      </Modal.Body>

      <Modal.Footer className="!justify-between gap-3 border-t border-slate-200/90 bg-card-color-100/80 pt-4 dark:border-slate-700 dark:bg-gray-900/40 sm:gap-4">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isUpdatingPregnancy}
          size="sm"
        >
          {tPM('buttons.cancel')}
        </Button>
        <div className="flex flex-wrap justify-end gap-2">
          {(() => {
            const isClosingHandoff =
              mode === 'edit-backend' &&
              !!onRequestClosePregnancy &&
              !!pregnancyFormValues.status &&
              pregnancyFormValues.status !== 'Active';
            const idleLabel = isClosingHandoff
              ? tPM('modals.pregnancy.buttons.continueClose')
              : mode === 'edit-backend'
                ? tPM('modals.pregnancy.buttons.update')
                : tPM('modals.pregnancy.buttons.save');
            const busyLabel = isClosingHandoff
              ? tPM('modals.pregnancy.buttons.opening')
              : mode === 'edit-backend'
                ? tPM('modals.pregnancy.buttons.updating')
                : tPM('modals.pregnancy.buttons.saving');
            return (
              <Button
                onClick={handleSave}
                disabled={isUpdatingPregnancy}
                size="sm"
                variant={
                  isClosingHandoff
                    ? 'primary'
                    : onSaveAndConfirm
                      ? 'secondary'
                      : 'primary'
                }
              >
                {isUpdatingPregnancy ? (
                  <span className="flex items-center gap-1.5">
                    <FiLoader className="animate-spin" size={14} /> {busyLabel}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    {isClosingHandoff ? (
                      <FiAlertTriangle size={14} />
                    ) : (
                      <FiCheckSquare size={14} />
                    )}{' '}
                    {idleLabel}
                  </span>
                )}
              </Button>
            );
          })()}
          {onSaveAndConfirm && (
            <Button
              variant="primary"
              onClick={handleSaveAndConfirm}
              disabled={isUpdatingPregnancy}
              size="sm"
            >
              {isUpdatingPregnancy ? (
                <span className="flex items-center gap-1.5">
                  <FiLoader className="animate-spin" size={14} />{' '}
                  {tPM('modals.pregnancy.buttons.processing')}
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <FiCheckSquare size={14} />{' '}
                  {tPM('modals.pregnancy.buttons.confirmAndSave')}
                </span>
              )}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PregnancyModal;
